// src/components/MultiScanner.js
// Scan one item after another (David, 10/29). Each scan opens a pop-up with
// the item and two buttons:
//   ✓ Correct  — the page keeps it, and the camera goes straight back to
//                scanning the next one
//   ↻ Re-scan  — dropped; scan again
// Finish is always on screen. Items can also be added by typing a tag or
// name when a label won't scan.
//
// Props:
//   lookup(code)  -> item       the scanned QR text -> item (throws if unknown)
//   search(text)  -> item|null  for typed tags / names
//   check(item)   -> { warn?, block? }  block = can't be added (shown, Re-scan only)
//   onCorrect(item)
//   count, finishLabel, onFinish, finishDisabled
import React, { useEffect, useRef, useState } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { detailUrl } from './PhotoUpload';

const READER_ID = 'multi-scan-reader';

export default function MultiScanner({ lookup, search, check, onCorrect, count, finishLabel, onFinish, finishDisabled = false }) {
  const [popup, setPopup] = useState(null);   // { item, warn, block } | { error }
  const [looking, setLooking] = useState(false);
  const [typed, setTyped] = useState('');
  const busyRef = useRef(false);               // a pop-up is open or a lookup is running
  const lastRef = useRef({ code: '', at: 0 }); // ignore the same label for a moment after Correct
  // The camera callback is set up once; read the page's latest functions
  // (and so its latest scanned list) through this ref.
  const live = useRef({});
  live.current = { lookup, search, check, onCorrect };

  useEffect(() => {
    let scanner = null;
    busyRef.current = false;   // React may start, stop and restart this in development
    const timer = setTimeout(() => {
      try {
        scanner = new Html5QrcodeScanner(READER_ID, {
          fps: 5, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0, showTorchButtonIfSupported: true,
        }, false);
        scanner.render(onScan, () => {});
      } catch (e) {
        console.error('Scanner init error:', e);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      busyRef.current = true;
      if (scanner) scanner.clear().catch(() => {});
    };
  }, []);

  const show = (item) => {
    const v = (live.current.check && live.current.check(item)) || {};
    setPopup({ item, warn: v.warn || null, block: v.block || null });
  };

  const onScan = async (text) => {
    const code = String(text || '').trim();
    if (!code || busyRef.current) return;
    if (code === lastRef.current.code && Date.now() - lastRef.current.at < 2500) return;
    busyRef.current = true;
    lastRef.current = { code, at: Date.now() };
    setLooking(true);
    try {
      show(await live.current.lookup(code));
    } catch {
      setPopup({ error: "This label isn't in the Asset Tracker." });
    } finally {
      setLooking(false);
    }
  };

  const addTyped = async () => {
    if (!typed.trim() || busyRef.current) return;
    busyRef.current = true;
    setLooking(true);
    try {
      const item = await live.current.search(typed.trim());
      if (item) { show(item); setTyped(''); } else setPopup({ error: `Nothing found for "${typed.trim()}".` });
    } catch {
      setPopup({ error: 'Search failed. Check your signal and try again.' });
    } finally {
      setLooking(false);
    }
  };

  const correct = () => {
    live.current.onCorrect(popup.item);
    lastRef.current = { code: lastRef.current.code, at: Date.now() };
    setPopup(null);
    busyRef.current = false;
  };

  const rescan = () => {
    lastRef.current = { code: '', at: 0 };   // the same label may be scanned again
    setPopup(null);
    busyRef.current = false;
  };

  const it = popup?.item;
  const holder = it && (it.assigned_to_name || it.assigned_to);
  const where = it && (it.current_location_name || it.current_location);

  return (
    <div className="multi-scanner">
      <div id={READER_ID} style={{ marginBottom: 8 }} />
      {looking && <p className="picker-status">Looking up…</p>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input className="input" style={{ flex: 1 }} placeholder="Label won't scan? Type a tag or name"
          value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTyped()} />
        <button className="btn btn-secondary" style={{ width: 'auto', padding: '0 16px', minHeight: 'auto' }}
          onClick={addTyped} disabled={!typed.trim()}>Add</button>
      </div>

      <div className="scan-finish-bar">
        <button className="btn btn-primary" onClick={onFinish} disabled={finishDisabled}>
          {finishLabel || `Finish (${count})`}
        </button>
      </div>

      {popup && (
        <div className="modal-overlay">
          <div className="modal-sheet scan-popup">
            <div className="modal-handle" />
            {popup.error ? (
              <>
                <div style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: 8 }}>❓</div>
                <p className="alert alert-error" style={{ marginBottom: 16 }}>{popup.error}</p>
                <button className="btn btn-primary" onClick={rescan}>↻ Re-scan</button>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 12 }}>
                  {it.photo_url
                    ? <img src={detailUrl(it.photo_url)} alt="" className="scan-popup-photo" />
                    : <div className="scan-popup-photo scan-popup-nophoto">📦</div>}
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ marginBottom: 4 }}>{it.name}</h3>
                    {it.asset_tag && <span className="asset-tag">{it.asset_tag}</span>}
                    {it.serial_number && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>S/N {it.serial_number}</p>}
                  </div>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                  {holder ? <>With <strong>{holder}</strong></> : 'Not with anyone'}
                  {where ? <> · {where}</> : null}
                </p>
                {popup.block && <p className="alert alert-error" style={{ marginBottom: 12 }}>{popup.block}</p>}
                {!popup.block && popup.warn && <p className="alert scan-popup-warn" style={{ marginBottom: 12 }}>⚠ {popup.warn}</p>}
                {!popup.block && (
                  <button className="btn btn-success" onClick={correct} style={{ marginBottom: 10 }}>✓ Correct</button>
                )}
                <button className={popup.block ? 'btn btn-primary' : 'btn btn-secondary'} onClick={rescan}>↻ Re-scan</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// The QR text on NGC labels is a link ending in /scan/<qr id>; older or
// hand-made labels may hold just the id, or an /item/<id> link.
export function qrIdFrom(text) {
  const t = String(text || '').trim();
  if (t.includes('/scan/')) return { qr: t.split('/scan/')[1].split('?')[0].trim() };
  if (t.includes('/item/')) return { itemId: t.split('/item/')[1].split('?')[0].trim() };
  return { qr: t };
}
